import { GlobalAlert } from '@thoughtspot/global-alert';
import { translate as t } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { FormBuilder, } from '@thoughtspot/radiant-react/widgets/form-builder';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useEffect, useMemo, useRef, useState, } from 'react';
import ReactDOM from 'react-dom';
import { useGetFeatureLazyQuery, useSetTscliCommand, } from "../../services/tscli/tscli-service";
import { BasePageContext } from "../../contexts/basepage-context";
import { Horizontal } from "../../utils/layouts";
import { LoaderIndicator } from '../loader';
import { Header, Seperator } from '../system/common';
import { Mode } from '../system/common/constants';
import { CSV_UPLOAD_CONNECTORS, featureId, featureName, ModalActions, TranslationKeys, tscliConfigOperationType, } from './constants';
import styles from './csv-upload.module.scss';
import { getEnabledConnectorIds, getViewMode, hasChanges, } from './csv-upload.util';
import { DisclaimerModal } from './disclaimer-modal/disclaimer-modal';
export const CsvUpload = ({ currentOrgId, }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [connectors, setConnectors] = useState(CSV_UPLOAD_CONNECTORS);
    const [formData, setFormData] = useState();
    const [configPostData, setConfigPostData] = useState(null);
    const [upgradeInProgress, setUpgradeInProgress] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [connectorsBeforeEdit, setConnectorsBeforeEdit] = useState([]);
    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        text: '',
        isError: false,
    });
    const [errorBannerConfig, setErrorBannerConfig] = useState({
        isVisible: false,
        text: '',
    });
    const [loaderConfig, setLoaderConfig] = useState({
        isLoading: false,
        loaderText: '',
    });
    const formBuilderRef = useRef(null);
    const isEdited = useRef(false);
    const { metricsService } = BasePageContext();
    const getTranslations = useMemo(() => (key) => t(`${key}`), [0]);
    const captureConfigurationChangeEvent = (configurationStatus) => {
        metricsService.collectEvent(new UserAction(userActionTypeEnum.CSV_UPLOAD_EMBRACE_CONFIGURED_FROM_ADMIN_PORTAL), {
            updatedStatus: configurationStatus,
        });
    };
    const turnOffLoaderAndEditMode = () => {
        setLoaderConfig({ ...loaderConfig, isLoading: false });
        setIsEditMode(false);
    };
    const onError = () => {
        turnOffLoaderAndEditMode();
        setErrorBannerConfig({
            ...errorBannerConfig,
            isVisible: true,
            text: getTranslations(TranslationKeys.FAILURE_BANNER_MESSAGE),
        });
    };
    const onComplete = (response) => {
        if (response.Data !== null) {
            setUpgradeInProgress(false);
            turnOffLoaderAndEditMode();
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                text: getTranslations(TranslationKeys.SUCCESS_TOASTER_MESSAGE),
            });
        }
        else {
            onError();
        }
    };
    const viewMode = getViewMode(currentOrgId);
    const { data: getFeatureQueryData, error: getFeatureQueryError, loading: getFeatureQueryLoading, getFeature, } = useGetFeatureLazyQuery(featureName, viewMode);
    useEffect(() => {
        getFeature();
    }, []);
    const [updateSetting] = useSetTscliCommand(onComplete, onError, featureName, configPostData, viewMode);
    const prepareConfigPostData = (enabledConnectors) => {
        const configData = {
            configOperation: enabledConnectors.length > 0
                ? tscliConfigOperationType.ADD
                : tscliConfigOperationType.RESET,
        };
        const configOptions = [
            {
                optionKey: featureId,
                optionValue: enabledConnectors.length > 0
                    ?
                        btoa(enabledConnectors.join(','))
                    : '',
                optionLabel: t(TranslationKeys.CSV_UPLOAD, { lng: 'en-US' }),
            },
        ];
        configData.configOptions = configOptions;
        setConfigPostData(configData);
    };
    useEffect(() => {
        if (getFeatureQueryLoading) {
            setLoaderConfig({
                ...loaderConfig,
                isLoading: true,
                loaderText: '',
            });
        }
        else {
            setLoaderConfig({
                ...loaderConfig,
                isLoading: false,
            });
            const configuredValue = getFeatureQueryData?.Data?.settings?.[0]?.configs?.[0]?.value;
            let enabledConnectorIds = [];
            if (configuredValue) {
                enabledConnectorIds = configuredValue
                    .split(',')
                    .map((connectorId) => connectorId.trim())
                    .filter((connectorId) => CSV_UPLOAD_CONNECTORS.some(connector => connector.id === connectorId));
            }
            const updatedConnectors = CSV_UPLOAD_CONNECTORS.map(connector => ({
                ...connector,
                enabled: enabledConnectorIds.includes(connector.id),
            }));
            setConnectors(updatedConnectors);
        }
    }, [getFeatureQueryLoading, getFeatureQueryData]);
    useEffect(() => {
        if (getFeatureQueryError) {
            setErrorBannerConfig({
                ...errorBannerConfig,
                isVisible: true,
                text: getTranslations(TranslationKeys.SETTINGS_GET_FAILURE_MESSAGE),
            });
        }
    }, [getFeatureQueryError]);
    useEffect(() => {
        if (formData) {
            const updatedConnectors = connectors.map((connector, index) => ({
                ...connector,
                enabled: formData[index]?.value || false,
            }));
            setConnectors(updatedConnectors);
            const enabledConnectorIds = getEnabledConnectorIds(updatedConnectors);
            const enabledConnectorIdsBeforeEdit = getEnabledConnectorIds(connectorsBeforeEdit);
            if (hasChanges(enabledConnectorIds, enabledConnectorIdsBeforeEdit)) {
                const enabledConnectorIdsBeforeEditSet = new Set(enabledConnectorIdsBeforeEdit);
                const newlyEnabledConnectorsIds = enabledConnectorIds.filter(id => !enabledConnectorIdsBeforeEditSet.has(id));
                if (newlyEnabledConnectorsIds.length) {
                    setIsConfirmationModalOpen(true);
                }
                else {
                    setLoaderConfig({
                        ...loaderConfig,
                        isLoading: true,
                        loaderText: getTranslations(TranslationKeys.LOADING_INDICATOR_MESSAGE),
                    });
                    prepareConfigPostData(enabledConnectorIds);
                }
            }
            else {
                setIsEditMode(false);
            }
        }
    }, [formData]);
    useEffect(() => {
        if (configPostData) {
            captureConfigurationChangeEvent(configPostData.configOperation === tscliConfigOperationType.ADD);
            setUpgradeInProgress(true);
            updateSetting();
        }
    }, [configPostData]);
    const onModalCloseCb = (modalAction) => {
        setIsConfirmationModalOpen(false);
        if (modalAction === ModalActions.CONFIRM) {
            setLoaderConfig({
                ...loaderConfig,
                isLoading: true,
                loaderText: getTranslations(TranslationKeys.LOADING_INDICATOR_MESSAGE),
            });
            const enabledConnectors = getEnabledConnectorIds(connectors);
            prepareConfigPostData(enabledConnectors);
        }
        else {
            setIsConfirmationModalOpen(false);
        }
    };
    const onSubmit = () => {
        setFormData(formBuilderRef?.current?.onSubmitForm());
    };
    const onCancel = () => {
        setIsEditMode(false);
        setConnectors(connectorsBeforeEdit);
    };
    const onEditButtonClick = () => {
        setIsEditMode(true);
        setConnectorsBeforeEdit([...connectors]);
        setToastConfig({
            ...toastConfig,
            isVisible: false,
            text: '',
        });
    };
    const formFields = useMemo(() => connectors.map(connector => ({
        id: connector.id,
        elementType: 'toggle',
        config: {
            label: connector.name,
        },
        value: connector.enabled,
        valueLabels: {
            true: getTranslations(TranslationKeys.STATUS_ENABLED),
            false: getTranslations(TranslationKeys.STATUS_DISABLED),
        },
    })), [connectors, getTranslations]);
    const saveButton = () => (React.createElement(Button, { onClick: onSubmit, text: getTranslations(TranslationKeys.SAVE_CHANGES_CTA), type: "primary", "data-testid": "saveButton" }));
    const cancelButton = () => (React.createElement(Button, { onClick: onCancel, text: getTranslations(TranslationKeys.CANCEL_CTA), type: "secondary", "data-testid": "cancelButton" }));
    const blockUserUI = () => upgradeInProgress
        ? ReactDOM.createPortal(React.createElement("div", { className: styles.editScreenBlockUsage }), document.body)
        : null;
    return (React.createElement(React.Fragment, null,
        blockUserUI(),
        React.createElement("div", { "data-testid": "csvUploadAdminPageBody" },
            React.createElement(DisclaimerModal, { isModalOpen: isConfirmationModalOpen, setIsModalOpen: onModalCloseCb }),
            loaderConfig.isLoading && (React.createElement(LoaderIndicator, { loadingText: loaderConfig.loaderText, hideLoader: !loaderConfig.isLoading })),
            React.createElement(GlobalAlert.Toast, { isVisible: toastConfig.isVisible, message: toastConfig.text, showCheckmarkIcon: !toastConfig.isError }),
            React.createElement(GlobalAlert.Banner, { isVisible: errorBannerConfig.isVisible, type: BannerAlertType.ERROR, message: errorBannerConfig.text }),
            !getFeatureQueryLoading && !getFeatureQueryError && (React.createElement(React.Fragment, null,
                React.createElement(Header, { title: getTranslations(TranslationKeys.CSV_UPLOAD_PAGE_TITLE), subTitle: getTranslations(TranslationKeys.CSV_UPLOAD_PAGE_DESCRIPTION), titleSize: "large", isFullWidth: true, mode: Mode.VIEW }),
                React.createElement(Seperator, null),
                React.createElement(Horizontal, { vAlignContent: "center", className: styles.headingContainer },
                    React.createElement(Typography, { variant: "p-large-bold" }, getTranslations(TranslationKeys.CSV_UPLOAD)),
                    !isEditMode && (React.createElement(Button, { id: "edit", text: getTranslations(TranslationKeys.EDIT_CTA), type: "secondary", backgroundType: "light", onClick: onEditButtonClick, "data-testid": "editCSVUploadStatus" }))),
                React.createElement(FormBuilder, { itemLayout: "horizontal", formFields: formFields, viewMode: isEditMode ? Mode.EDIT : Mode.VIEW, isEdited: (edited) => {
                        isEdited.current = edited;
                    }, ref: formBuilderRef }),
                isEditMode && (React.createElement("div", { className: styles.actionButtonContainer },
                    cancelButton(),
                    saveButton())))))));
};
//# sourceMappingURL=csv-upload.container.js.map